import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  loadCalendarSummaries,
  loadOrganizations,
  organizationChosen,
  signOut,
} from '@/redux/session-slice';
import { Button, Card, Eyebrow, Initials, LoadingLine, Notice, StatusChip, TextButton } from '@/components/ui';
import { Logo } from '@/components/Brand';
import type { OrganizationConnectionSummary } from '@/lib/connections';
import type { Organization } from '@/lib/types';

/**
 * Screen 2 — the organization picker.
 *
 * Cards with initials, name, domain, provider and space count. Skipped
 * entirely when the person has one organization (handled in the session slice,
 * so the decision is not duplicated here). The switcher in the sidebar is how
 * they come back.
 *
 * WHICH CALENDAR IS CONNECTED COMES FROM A SECOND READ, not from the
 * organization row. The list is ZenCore's `GET /organizations` passed straight
 * through, and ZenCore knows nothing about this service's connections — so the
 * card used to say "No calendar connected" for every organization, including
 * ones syncing happily. `GET /calendar/connections/summary` is this service
 * answering for itself, and until it has answered the chip says "Checking",
 * because claiming "none" before we know is the same wrong answer in a
 * different costume.
 */
export function OrganizationPicker() {
  const dispatch = useAppDispatch();
  const { user, organizations, loadingOrganizations, error, calendarByOrganization, calendarStatus } =
    useAppSelector((s) => s.session);
  const [filter, setFilter] = useState('');

  // Signing in through the form gives us a token and a user, but not the
  // organization list — `restoreSession` only runs for a session restored from
  // storage. Without this the first thing a new sign-in sees is "you are not in
  // any organisation yet", which is both wrong and alarming.
  useEffect(() => {
    if (organizations.length === 0 && !loadingOrganizations && !error) {
      void dispatch(loadOrganizations());
    }
  }, [organizations.length, loadingOrganizations, error, dispatch]);

  // One read for every card, and only once the cards exist. Not chained onto
  // the organization list, which is also filled by `restoreSession`.
  useEffect(() => {
    if (organizations.length > 0 && calendarStatus === 'idle') {
      void dispatch(loadCalendarSummaries());
    }
  }, [organizations.length, calendarStatus, dispatch]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return organizations;
    return organizations.filter(
      (o) =>
        o.name.toLowerCase().includes(needle) || (o.domain ?? '').toLowerCase().includes(needle),
    );
  }, [organizations, filter]);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="zs-on-dark flex h-[73px] items-center justify-between gap-[20px] bg-ink px-[20px]">
        <Logo width={163} />
        <div className="flex items-center gap-[12px]">
          {user?.email && (
            <span className="hidden truncate text-sub text-sky-pale sm:block">{user.email}</span>
          )}
          <button
            type="button"
            onClick={() => void dispatch(signOut())}
            className="rounded-pill border border-white/25 px-[16px] py-[8px] text-caption font-semibold text-page transition hover:border-green-soft hover:text-green-soft"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="animate-rise mx-auto flex w-full max-w-[960px] flex-1 flex-col items-center gap-[36px] px-[20px] py-[56px]">
        <div className="flex max-w-[560px] flex-col gap-[10px] text-center">
          <Eyebrow>Organizations</Eyebrow>
          <h1 className="m-0 text-title font-semibold">
            Choose an organization to manage
          </h1>
          <p className="m-0 text-lead text-muted">
            Each organization has its own ZenCore account, calendar connection and space mapping —
            nothing is shared between them.
          </p>
        </div>

        {error && (
          <div className="w-full max-w-[560px]">
            <Notice tone="broken" title="We could not load your organizations">
              {error}{' '}
              <TextButton type="button" onClick={() => void dispatch(loadOrganizations())}>
                Try again
              </TextButton>
            </Notice>
          </div>
        )}

        {organizations.length > 6 && (
          <label className="flex w-full max-w-[420px] flex-col gap-[6px]">
            <span className="zs-sr-only">Find an organization</span>
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by name or domain"
              className="rounded-pill border border-divider bg-card px-[18px] py-[11px] text-sub transition-colors focus:border-sky focus:outline-none"
            />
          </label>
        )}

        {loadingOrganizations && organizations.length === 0 ? (
          <LoadingLine>Loading your organizations…</LoadingLine>
        ) : organizations.length === 0 ? (
          <Card className="max-w-[520px] text-center">
            <h2 className="m-0 text-subheading font-semibold">You are not in any organization yet</h2>
            <p className="mt-[8px] mb-[20px] text-sub text-muted">
              Calendar sync is set up per organization. Ask your ZenSpace administrator to add you
              to one, then sign in again.
            </p>
            <Button tone="quiet" onClick={() => void dispatch(signOut())}>
              Sign out
            </Button>
          </Card>
        ) : (
          <ul className="m-0 grid w-full list-none grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-[20px] p-0">
            {visible.map((organization) => (
              <li key={organization.id}>
                <OrganizationCard
                  organization={organization}
                  connections={calendarByOrganization[organization.id] ?? []}
                  calendarStatus={calendarStatus}
                  onChoose={() => dispatch(organizationChosen(organization.id))}
                />
              </li>
            ))}
          </ul>
        )}

        {organizations.length > 0 && visible.length === 0 && (
          <p className="text-body text-muted">Nothing matches “{filter.trim()}”.</p>
        )}
      </main>
    </div>
  );
}

function OrganizationCard({
  organization,
  connections,
  calendarStatus,
  onChoose,
}: {
  organization: Organization;
  connections: OrganizationConnectionSummary['connections'];
  calendarStatus: 'idle' | 'loading' | 'ready' | 'failed';
  onChoose: () => void;
}) {
  // The headline of the first connection that is not simply working. Shown
  // under the chips so "Microsoft 365" in amber says what it is amber ABOUT —
  // a chip colour on its own is a puzzle, and this is the same sentence the
  // Connections screen uses for the same state.
  const needsAttention = connections.find((c) => c.tone !== 'healthy') ?? null;

  return (
    // The whole card is the control, so it is one tab stop and one click target
    // rather than a card containing a small button.
    <button
      type="button"
      onClick={onChoose}
      className="flex h-full w-full cursor-pointer flex-col gap-[14px] rounded-feature border-0 bg-card p-[28px] text-left shadow-card transition duration-200 ease-zs hover:-translate-y-[4px] hover:shadow-lift"
    >
      <Initials name={organization.name} />
      <div className="min-w-0">
        <p className="m-0 truncate text-lead font-semibold">
          {organization.name}
        </p>
        <p className="mt-[2px] mb-0 truncate text-sub text-muted">
          {organization.domain ?? 'No domain on record'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-[8px]">
        {calendarStatus === 'idle' || calendarStatus === 'loading' ? (
          <StatusChip tone="neutral">Checking calendar…</StatusChip>
        ) : calendarStatus === 'failed' ? (
          // We could not ask. Saying "no calendar connected" here would be a
          // guess, and the wrong one for every organization that has one.
          <StatusChip tone="neutral">Calendar status unavailable</StatusChip>
        ) : connections.length === 0 ? (
          <StatusChip tone="neutral">No calendar connected</StatusChip>
        ) : (
          // An organization can hold Google and Microsoft at once, so this is
          // every live connection rather than the first one.
          connections.map((connection) => (
            <StatusChip key={connection.provider} tone={connection.tone}>
              {connection.display_name}
            </StatusChip>
          ))
        )}
        {typeof organization.space_count === 'number' && (
          <span className="rounded-chip bg-divider px-[10px] py-[4px] text-micro font-medium">
            {organization.space_count} {organization.space_count === 1 ? 'space' : 'spaces'}
          </span>
        )}
      </div>

      {needsAttention && (
        <p className="m-0 text-caption text-muted">{needsAttention.headline}</p>
      )}

      {organization.user_role_name && (
        <p className="m-0 text-caption text-muted">
          You are {formatRole(organization.user_role_name)} here
        </p>
      )}
    </button>
  );
}

/** `org_admin` reads as a database value; "an administrator" reads as English. */
function formatRole(role: string): string {
  const spelled = role.replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (spelled.includes('admin')) return 'an administrator';
  if (spelled.includes('manager')) return 'a manager';
  if (spelled.includes('member')) return 'a member';
  return `a ${spelled}`;
}
